/** @prettier */

import {Grid, Box, Stack, Tab} from '@mui/material';
import {useEffect, useState, ReactElement} from 'react';
import {useParams} from 'react-router-dom';
import {isUndefined} from 'lodash';
import {useTranslation} from 'react-i18next';
import {TFunction} from 'i18next';
import {utcToZonedTime, format, zonedTimeToUtc} from 'date-fns-tz';
import {TabContext, TabPanel, TabList} from '@mui/lab';

//API import
import {
  getCompositionsRTUnitByUnitID,
  getStatesSitesRt,
  getCompositionsSitesBySiteID,
  getSettingUser,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';
//Type import
import {graphProps} from '../../Type/TypeDef';

import {useValueRef} from '../../functions/useValueRef';
import {
  invertComma,
  initialUpper,
  roundOff,
  monthTranslation,
} from '../../functions/convertString';
import {iconBlack, iconWhite, iconRed} from '../../define';

/**
 * チラー詳細
 * @param props props
 * @returns JSX.Element
 */
const ChillerDetail = (props: {
  /** グラフに描画するデータをセットする */
  setGraphParam: graphProps['setGraphParam'];
  /** 詳細画面のタブ切り替え変数 */
  tabType: 'diagram' | 'bar' | 'line' | 'barLineMixed';
  /** 詳細画面のタブ切り替え変数（set関数） */
  setTabType: React.Dispatch<
    React.SetStateAction<'line' | 'bar' | 'barLineMixed' | 'diagram'>
  >;
}) => {
  const trans = useTranslation().t; //翻訳関数
  //URLパラメータの取得
  const {siteID, targetID: unitID} = useParams();
  //ゲートウェイIDの取得
  let gwID = '';
  if (unitID !== undefined) {
    const validateID = unitID.split('+'); //[0]：ユニットの種類、[1]：ゲートウェイID、[2]：rtID
    gwID = validateID[1];
  }

  //prettier-ignore
  //Chillerの登録情報
  const [unitData, setUnitData] = useState<apiIF.resParameters.rtUnitInformations | null>(null);
  //prettier-ignore
  //Chillerの状態値
  const [statesData, setStatesData] = useState<apiIF.resParameters.rtOUStates | null>(null);
  //現在時刻（状態値の定期更新用）
  const [nowTime, setNowTime] = useState(new Date());
  //タイムゾーン
  const [timeZone, setTimeZone] = useState('');
  //prettier-ignore
  //圧力単位
  const [pressureUnit, setPressureUnit] = useState<apiIF.res.getSettingUser['unit']['pressure']>('MPa');
  //API応答待ちフラグ
  const [apiWait, setApiWait] = useState(false);

  /** グラフアイコンの切り替えテーブル */
  const graphIconColor: {
    [type in 'diagram' | 'bar' | 'line' | 'barLineMixed']: {
      barLineMixed: string;
      bar: string;
      line: string;
    };
  } = {
    diagram: {
      barLineMixed: iconBlack.BAR_GRAPH.src,
      bar: iconBlack.BAR_GRAPH.src,
      line: iconBlack.LINE_GRAPH.src,
    },
    barLineMixed: {
      barLineMixed: iconRed.BAR_GRAPH.src,
      bar: iconBlack.BAR_GRAPH.src,
      line: iconBlack.LINE_GRAPH.src,
    },
    bar: {
      barLineMixed: iconBlack.BAR_GRAPH.src,
      bar: iconRed.BAR_GRAPH.src,
      line: iconBlack.LINE_GRAPH.src,
    },
    line: {
      barLineMixed: iconBlack.BAR_GRAPH.src,
      bar: iconBlack.BAR_GRAPH.src,
      line: iconRed.LINE_GRAPH.src,
    },
  };

  /** 状態値取得用APIのクエリ */
  const statesAPIQuery: apiIF.reqParameters.getStatesSitesRt = {
    fields:
      'unit-name,run-stop,operation-mode,preset-temperature,' +
      'error-code,compressor-run-stop,compressor-run-stop2,' +
      'high-pressure,high-pressure2,low-pressure,low-pressure2,' +
      'outlet-water-temperature,flow-rate,inlet-water-temperature',
  };
  /** 当日分の熱量COP取得用APIのクエリ */
  const energyAPIQuery: apiIF.reqParameters.getStatesSitesRt = {
    'fields': 'energy-amount,cop',
    'every-minute': 1440,
  };
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (isUndefined(siteID) || isUndefined(unitID)) return;

    setApiWait(true);

    const promises = [];
    //登録情報を取得
    promises.push(getCompositionsRTUnitByUnitID(siteID, gwID, unitID));
    //圧力単位を取得
    promises.push(getSettingUser());
    //サイトのタイムゾーンを取得
    promises.push(getCompositionsSitesBySiteID(siteID));

    Promise.all(promises)
      .then(info => {
        /** 熱源機の情報 */
        const rtInfo = info[0] as apiIF.res.getCompositionsRTUnitByUnitID;
        /** ユーザの情報 */
        const userInfo = info[1] as apiIF.res.getSettingUser;
        /** サイトの情報 */
        const siteInfo = info[2] as apiIF.res.getCompositionsSitesBySiteID;

        setUnitData(rtInfo);
        setPressureUnit(userInfo.unit.pressure);
        setTimeZone(siteInfo.timezone);

        //状態値を取得
        const statesPromises = [];
        //熱量とCOP以外の状態値を取得
        statesPromises.push(getStatesSitesRt(siteID, statesAPIQuery));
        /** 状態値を取得する範囲の開始時刻 */
        const afterDate = utcToZonedTime(new Date(), siteInfo.timezone);
        afterDate.setHours(0, 0, 0, 0);
        statesPromises.push(
          getStatesSitesRt(siteID, {
            ...energyAPIQuery,
            'after-time': zonedTimeToUtc(
              afterDate,
              siteInfo.timezone
            ).toISOString(),
          })
        );
        return Promise.all(statesPromises);
      })
      .then(response => {
        /** 熱量とCOP以外の状態値 */
        const states = response[0]['rt-states'];
        /** 当日分の熱量とCOP */
        const energy = response[1]['rt-states'];

        const state = searchLatestState(unitID, states, energy);
        if (state !== null) {
          setStatesData(state);
        }
      })
      .finally(() => {
        setApiWait(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitID, siteID, gwID]);

  /** タイマ動作後にunitIDが変わった場合、1分後に参照するsiteIDはタイマ動作開始時のunitIDとなるため */
  /** useRefを使って、現在のunitIDを参照するようにしている。 */
  const nowUnit = useValueRef(unitID);
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (isUndefined(siteID)) return;

    const intervalId = setInterval(() => {
      setNowTime(new Date());
      const promises = [];
      //熱量とCOP以外の状態値を取得
      promises.push(getStatesSitesRt(siteID, statesAPIQuery));
      //当日分の熱量とCOPを取得
      /** 状態値を取得する範囲の開始時刻 */
      const afterDate = utcToZonedTime(new Date(), timeZone);
      afterDate.setHours(0, 0, 0, 0);
      promises.push(
        getStatesSitesRt(siteID, {
          ...energyAPIQuery,
          'after-time': zonedTimeToUtc(afterDate, timeZone).toISOString(),
        })
      );
      Promise.all(promises).then(response => {
        /** 熱量とCOP以外の状態値 */
        const states = response[0]['rt-states'];
        /** 当日分の熱量とCOP */
        const energy = response[1]['rt-states'];

        if (nowUnit.current !== undefined) {
          const state = searchLatestState(nowUnit.current, states, energy);
          if (state !== null) {
            setStatesData(state);
          }
        }
      });
    }, 60000);

    return function () {
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowTime, nowUnit]);

  /**
   * 最新の状態値を検索
   * @param unitID 検索する熱源機のID
   * @param states 熱量とCOP以外の状態値
   * @param energy 当日分の熱量とCOP
   * @returns 最新の状態値
   */
  const searchLatestState = (
    unitID: string,
    states: apiIF.res.getStatesSitesRt['rt-states'],
    energy: apiIF.res.getStatesSitesRt['rt-states']
  ) => {
    if (states.length > 0 && energy.length > 0) {
      /** 熱量とCOP以外の状態値 */
      const unitState = states[0]['outdoor-unit-states'].find(data => {
        return data['unit-id'] === unitID;
      });
      /** 当日分の熱量とCOP */
      const unitEnergy = energy[0]['outdoor-unit-states'].find(data => {
        return data['unit-id'] === unitID;
      });
      if (unitState !== undefined && unitEnergy !== undefined) {
        return {...unitState, ...unitEnergy};
      }
    }
    return null;
  };

  /**
   * タブ変更時
   * @param event
   * @param newValue
   */
  const handleTabChange = (
    event: React.SyntheticEvent,
    newValue: 'bar' | 'line' | 'barLineMixed' | 'diagram',
    unitID: string,
    unitName: string | undefined
  ) => {
    props.setTabType(newValue);
    if (newValue === 'diagram') {
      props.setGraphParam('');
    } else {
      props.setGraphParam('chiller', unitID, newValue, unitName);
    }
  };

  /**
   * エラーコードに基づいてエラー出力とアイコンを表示
   * @param errorCode - 受信したエラーコード
   * @returns JSX.Element
   */
  const ErrorDisplay = (errorCode: string | undefined) => {
    if (errorCode === undefined || errorCode === '00') {
      return '―';
    } else if (errorCode === '100') {
      return trans('ChillerDetail.item.value.err.disconnected'); //通信異常時
    } else if (errorCode === '') {
      return <></>; // 空文字の場合は無課金なので何も表示しない
    } else {
      return (
        <Grid
          container
          columnSpacing={1}
          justifyContent="center"
          alignItems="center">
          <Grid item xs="auto">
            <img
              src={iconBlack.ERROR.src}
              alt={
                trans(
                  `ChillerDetail.image.alternativeText.${iconBlack.ERROR.alt}`
                ) || ''
              }
              className="unitArea__stateList__state__value__icon--error"
            />
          </Grid>
          <Grid item xs="auto" id="mv-qtonDetailDataErrorCode">
            {`${trans('ChillerDetail.item.value.err.errCode')}${errorCode}`}
          </Grid>
        </Grid>
      );
    }
  };

  /**
   * 運転/停止アイコン
   * @param props props
   * @returns JSX.Element
   */
  const RunStopIcon = (props: {
    /** 運転/停止 */
    runStop: apiIF.resParameters.rtOUStates['run-stop'];
  }) => {
    if (props.runStop === undefined) {
      return <></>;
    }

    /**
     * 運転/停止アイコン用クラス名
     * @param runStopState 現在の状態
     * @returns クラス名
     */
    const runStopIconClassName = (
      runStopState: apiIF.resParameters.rtOUStates['run-stop']
    ) => {
      switch (runStopState) {
        case 'run':
          return '';
        case 'stop':
          return '';
        default:
          return 'unitArea__stateList__state__value__icon__runStopIcon--stop';
      }
    };
    /**
     * 運転/停止アイコン画像用クラス名
     * @param runStopState 現在の状態
     * @returns クラス名
     */
    const runStopIconImageClassName = (
      runStopState: apiIF.resParameters.rtOUStates['run-stop']
    ) => {
      switch (runStopState) {
        case 'run':
          return 'unitArea__stateList__state__value__icon__runStopIcon__image';
        case 'stop':
          return 'unitArea__stateList__state__value__icon__runStopIcon__image';
        default:
          return 'unitArea__stateList__state__value__icon__runStopIcon__smallImage';
      }
    };
    /**
     * 運転/停止用アイコン一覧
     * @param runStop 現在の状態
     * @returns アイコン
     */
    const runStopIconSource = (
      runStop: apiIF.resParameters.rtOUStates['run-stop']
    ) => {
      switch (runStop) {
        case 'run':
          return iconBlack.HOT_WATER_RUNNING.src;
        case 'stop':
          return iconBlack.HOT_WATER_STOPPED.src;
        default:
          return iconBlack.MISSING.src;
      }
    };

    return (
      <Box className={runStopIconClassName(props.runStop)}>
        <img
          id="mv-chillerDetailDataRunStop"
          className={runStopIconImageClassName(props.runStop)}
          src={runStopIconSource(props.runStop)}
          title={`${trans(
            `ChillerDetail.image.title.powerIcon.${props.runStop}`,
            {defaultValue: initialUpper(props.runStop as string)} //undefinedでないことは確認しているため問題ない
          )}`}
          alt={`${trans('ChillerDetail.image.alternativeText.powerIcon')}`}
        />
      </Box>
    );
  };

  /**
   * 運転モードアイコン
   * @param props props
   * @returns JSX.Element
   */
  const OperationModeIcon = (props: {
    /** 運転モード */
    operationMode: apiIF.resParameters.rtOUStates['operation-mode'];
  }) => {
    if (
      isView(props.operationMode) === false ||
      (props.operationMode !== 'cooling' && props.operationMode !== 'heating')
    ) {
      return (
        <Grid item xs="auto">
          --
        </Grid>
      );
    }

    /**
     * 運転モード用アイコン一覧
     * @param operationMode 現在の運転モード
     * @returns アイコン
     */
    const getOperationModeIcon = (
      operationMode: apiIF.resParameters.rtOUStates['operation-mode']
    ) => {
      switch (operationMode) {
        case 'heating':
          return {
            src: iconWhite.HEATING.src,
            alt: trans(
              `ChillerDetail.image.alternativeText.${iconWhite.HEATING.alt}`
            ),
          };
        case 'cooling':
          return {
            src: iconWhite.COOLING.src,
            alt: trans(
              `ChillerDetail.image.alternativeText.${iconWhite.COOLING.alt}`
            ),
          };
        default:
          return {
            src: iconBlack.MISSING.src,
            alt: trans(
              `ChillerDetail.image.alternativeText.${iconBlack.MISSING.alt}`
            ),
          };
      }
    };
    /**
     * 運転モード変換テーブル
     * @param mode 現在の状態
     * @returns 変換した結果
     */
    const operationModeDisplayText = (
      mode: apiIF.resParameters.rtOUStates['operation-mode']
    ) => {
      switch (mode) {
        case 'cooling':
          return trans('ChillerDetail.item.value.operationMode.cooling');
        case 'heating':
          return trans('ChillerDetail.item.value.operationMode.heating');
        default:
          return '';
      }
    };

    return (
      <>
        <Grid item xs="auto">
          <Box
            className={
              props.operationMode === 'heating' ||
              props.operationMode === 'cooling'
                ? 'unitArea__stateList__state__value__icon--operationMode__border'
                : 'unitArea__stateList__state__value__icon--operationMode__noMode'
            }>
            <img
              id="mv-chillerDetailDataOperationModeImg"
              className="unitArea__stateList__state__value__icon--operationMode__border__image"
              src={getOperationModeIcon(props.operationMode).src}
              title={
                props.operationMode === 'heating' ||
                props.operationMode === 'cooling'
                  ? `${trans(
                      `ChillerDetail.image.title.operationMode.${props.operationMode}`
                    )}`
                  : `${trans('ChillerDetail.image.title.operationMode.none')}`
              }
              alt={getOperationModeIcon(props.operationMode).alt}
            />
          </Box>
        </Grid>
        <Grid
          item
          xs="auto"
          className="unitArea__stateList__state__value--mode">
          {operationModeDisplayText(props.operationMode)}
        </Grid>
      </>
    );
  };

  /** 現在の日付を翻訳して表示
   * @returns 現在日の日付表示
   */
  const displayNowDate = () => {
    /** 月名一覧 */
    const monthNameTable = [
      monthTranslation('january', trans),
      monthTranslation('february', trans),
      monthTranslation('march', trans),
      monthTranslation('april', trans),
      monthTranslation('may', trans),
      monthTranslation('june', trans),
      monthTranslation('july', trans),
      monthTranslation('august', trans),
      monthTranslation('september', trans),
      monthTranslation('october', trans),
      monthTranslation('november', trans),
      monthTranslation('december', trans),
    ];

    /** 現在の日付を取得 */
    const nowDate = utcToZonedTime(new Date(), timeZone);
    /** 現在日の日付をフォーマット化して日と年を取得 */
    const [day, , year] = format(nowDate, 'dd.MMMM yyyy').split(/[. ]/);
    /** 現在日の月の月名を取得 */
    const month = monthNameTable[nowDate.getMonth()];

    return `${day}.${month} ${year}`;
  };

  //Chillerの状態値が無い場合は表示しない
  if (statesData === null || unitData === null) return <></>;
  return (
    <Stack spacing={5}>
      <Box className="unitArea__title">
        <Box className="unitArea__title__name" title={statesData['unit-name']}>
          {`${trans('ChillerDetail.title')} ${statesData['unit-name']}`}
        </Box>
      </Box>
      <Box className="unitArea__stateList">
        <Grid container alignItems="flex-end" rowSpacing={2}>
          {/** 運転/停止 */}
          <StateContents
            title={trans('ChillerDetail.item.title.power')}
            value={<RunStopIcon runStop={statesData['run-stop']} />}
          />
          {/** 運転モード */}
          <StateContents
            title={trans('ChillerDetail.item.title.operationMode')}
            value={
              <Grid
                container
                columnSpacing={1}
                justifyContent="center"
                alignItems="center">
                <OperationModeIcon
                  operationMode={statesData['operation-mode']}
                />
              </Grid>
            }
          />
          {/** 設定温度 */}
          <StateContents
            title={trans('ChillerDetail.item.title.setTemp')}
            value={
              <Box id="mv-qtonDetailDataSetTemp">
                {isView(statesData['preset-temperature'])
                  ? invertComma(
                      //以下コンパイルエラーになってしまうため強制的に文字列にしている
                      //isView()で確認しているため問題ない
                      roundOff(statesData['preset-temperature'] as string, 1)
                    )
                  : '--'}
                <Box
                  component="span"
                  className="unitArea__stateList__state__value__unit--degreeC">
                  &nbsp;{trans('ChillerDetail.item.value.setTemp.unit')}
                </Box>
              </Box>
            }
          />
          {/** 熱量 */}
          <StateContents
            title={trans('ChillerDetail.item.title.energyAmount')}
            value={
              <Box
                id="mv-qtonDetailDataEnergyAmount"
                className="unitArea__stateList__state__text">
                {statesData['energy-amount'] !== undefined &&
                (isView(statesData['energy-amount']['cooling-mode']) ||
                  isView(statesData['energy-amount']['heating-mode']))
                  ? //prettier-ignore
                    invertComma(
                      roundOff(
                        String(
                          (Number(statesData['energy-amount']['cooling-mode']) + Number(statesData['energy-amount']['heating-mode'])) / 1000
                        ),
                        1
                      )
                    )
                  : '--'}
                <Box
                  component="span"
                  className="unitArea__stateList__state__value__unit--watt">
                  &nbsp;{trans('ChillerDetail.item.value.energyAmount.unit')}
                </Box>
              </Box>
            }
          />
          {/** COP */}
          <StateContents
            title={trans('ChillerDetail.item.title.cop')}
            value={
              <Box id="mv-qtonDetailDataCop">
                {isView(statesData['cop'])
                  ? //以下コンパイルエラーになってしまうため強制的に文字列にしている
                    //isView()で確認しているため問題ない
                    invertComma(roundOff(statesData['cop'] as string, 1))
                  : '--'}
              </Box>
            }
          />
          {/** エラーコード */}
          <StateContents
            title={trans('ChillerDetail.item.title.err')}
            value={ErrorDisplay(statesData['error-code'])}
          />
        </Grid>
      </Box>
      <TabContext value={props.tabType}>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          rowSpacing={1}
          className="unitArea__tabs">
          <Grid item xs={10} xl={2} className="unitArea__tabs__date">
            {/* 現在の日付を月名を翻訳して表示 */}
            {displayNowDate()}
          </Grid>
          <Grid item xs={12} lg={11} xl={9}>
            <TabList
              variant="fullWidth"
              className="unitArea__tabs__tabList"
              onChange={(
                event: React.SyntheticEvent,
                newValue: 'bar' | 'line' | 'barLineMixed' | 'diagram'
              ) => {
                handleTabChange(
                  event,
                  newValue,
                  statesData['unit-id'],
                  statesData['unit-name']
                );
              }}>
              <Tab
                label={`${trans(
                  'ChillerDetail.diagram.header.tab.waterCircuitDiagram'
                )}`}
                value="diagram"
                className="unitArea__tabs__tabList__tab"
                disabled={apiWait}
              />
              <Tab
                label={`${trans(
                  'ChillerDetail.diagram.header.tab.energyAmount'
                )}`}
                value="barLineMixed"
                disabled={apiWait || statesData['error-code'] === ''} //エラーコードが空文字の場合対象のユニットは無課金GWのユニットなので無効化
                icon={
                  <img
                    className="unitArea__tabs__tabList__icon"
                    src={graphIconColor[props.tabType].barLineMixed}
                    alt={`${trans(
                      'ChillerDetail.image.alternativeText.barLineMixed'
                    )}`}
                  />
                }
                iconPosition="start"
                className="unitArea__tabs__tabList__tab"
              />
              <Tab
                label={`${trans(
                  'ChillerDetail.diagram.header.tab.powerConsumption'
                )}`}
                value="bar"
                disabled={apiWait || statesData['error-code'] === ''} //エラーコードが空文字の場合対象のユニットは無課金GWのユニットなので無効化
                icon={
                  <img
                    className="unitArea__tabs__tabList__icon"
                    src={graphIconColor[props.tabType].bar}
                    alt={`${trans(
                      'ChillerDetail.image.alternativeText.barGraph'
                    )}`}
                  />
                }
                iconPosition="start"
                className="unitArea__tabs__tabList__tab"
              />
              <Tab
                label={`${trans(
                  'ChillerDetail.diagram.header.tab.trendReporting'
                )}`}
                value="line"
                disabled={apiWait || statesData['error-code'] === ''} //エラーコードが空文字の場合対象のユニットは無課金GWのユニットなので無効化
                icon={
                  <img
                    className="unitArea__tabs__tabList__icon"
                    src={graphIconColor[props.tabType].line}
                    alt={`${trans(
                      'ChillerDetail.image.alternativeText.lineGraph'
                    )}`}
                  />
                }
                iconPosition="start"
                className="unitArea__tabs__tabList__tab"
              />
            </TabList>
          </Grid>
        </Grid>
        <TabPanel value="diagram">
          <Box className="unitArea__diagram">
            <Grid container justifyContent="center">
              <Grid item xs="auto" className="unitArea__diagram__background">
                {/** コンプレッサー */}
                {/** capacityは５０固定だが将来的な仕様追加を想定してコンプレッサー2台表示コンポーネントは残しておく */}
                {/* {unitData['capacity'] === '100' ? (
                  <MultiComp
                    statesData={statesData}
                    pressureUnit={pressureUnit}
                    trans={trans}
                  />
                ) : ( */}
                <SingleComp
                  statesData={statesData}
                  pressureUnit={pressureUnit}
                  trans={trans}
                />
                {/* )} */}
                {/** 貯湯量タンク */}
                <WaterTank statesData={statesData} trans={trans} />
              </Grid>
            </Grid>
          </Box>
        </TabPanel>
      </TabContext>
    </Stack>
  );
};

/**
 * 状態値チェック
 * @param statesData 熱源機の状態値
 * @returns 状態値の判定式
 */
const isView = (stateData: string | undefined) => {
  return stateData !== undefined && stateData !== '';
};

/**
 * 状態値
 * @param props props
 * - title タイトル
 * - value 状態値
 * @returns JSX
 */
const StateContents = (props: {
  title: string;
  value: string | ReactElement;
}) => {
  return (
    <Grid item xs={4} xl={2} className="unitArea__stateList__state">
      <Stack spacing={3}>
        <Box className="unitArea__stateList__state__title">{props.title}</Box>
        <Box className="unitArea__stateList__state__value">
          <Box>{props.value}</Box>
        </Box>
      </Stack>
    </Grid>
  );
};

/**
 * コンプレッサー表示
 * @param statesData 親機の状態値
 * @param compressor コンプレッサー名
 * @param pressureUnit 圧力単位
 * @param trans 翻訳関数
 * @returns JSX
 */
const CompPressure = (props: {
  statesData: apiIF.resParameters.rtOUStates;
  compressor: 'CM1' | 'CM2';
  pressureUnit: apiIF.res.getSettingUser['unit']['pressure'];
  trans: TFunction;
}) => {
  /** 運転中かどうか */
  const isRun =
    props.compressor === 'CM1'
      ? props.statesData['compressor-run-stop'] === 'run'
      : props.statesData['compressor-run-stop2'] === 'run';

  return (
    <Grid container justifyContent="center">
      <Grid item xs="auto">
        <Stack className="unitArea__diagram__background__comp__data__pressure">
          {/** タイトル */}
          <Box
            className={
              isRun
                ? 'unitArea__diagram__background__comp__data__pressure__title--run'
                : 'unitArea__diagram__background__comp__data__pressure__title--stop'
            }>
            {props.compressor === 'CM1'
              ? props.trans(
                  'ChillerDetail.diagram.body.compressorName.compressor1'
                )
              : props.trans(
                  'ChillerDetail.diagram.body.compressorName.compressor2'
                )}
          </Box>
          {/** 高圧 */}
          <Grid
            container
            alignItems="center"
            columnSpacing={1}
            className={`unitArea__diagram__background__comp__data__pressure__box unitArea__diagram__background__comp__data__pressure__box--hp ${
              isRun
                ? ''
                : 'unitArea__diagram__background__comp__data__pressure__box--stop'
            }`}>
            <Grid item xs="auto">
              <Box
                className={
                  isRun
                    ? 'unitArea__diagram__background__comp__data__pressure__box__mark--run'
                    : 'unitArea__diagram__background__comp__data__pressure__box__mark--stop'
                }>
                {props.trans(
                  'ChillerDetail.diagram.body.compressorHighPressure'
                )}
              </Box>
            </Grid>
            {props.compressor === 'CM1' ? (
              <Grid
                item
                xs
                className="unitArea__diagram__background__comp__data__pressure__box__number">
                {isView(props.statesData['high-pressure'])
                  ? `${invertComma(
                      //以下コンパイルエラーになってしまうため強制的に文字列にしている
                      //isView()で確認しているため問題ない
                      roundOff(props.statesData['high-pressure'] as string, 1)
                    )}`
                  : '--'}
                <Box
                  component="span"
                  className="unitArea__diagram__background__comp__data__pressure__box__number__unit">
                  &nbsp;
                  {props.pressureUnit === 'MPa'
                    ? props.trans(
                        'ChillerDetail.diagram.body.unit.pressure.mpa'
                      )
                    : props.trans(
                        'ChillerDetail.diagram.body.unit.pressure.bar'
                      )}
                </Box>
              </Grid>
            ) : (
              <Grid
                item
                xs
                className="unitArea__diagram__background__comp__data__pressure__box__number">
                {isView(props.statesData['high-pressure2'])
                  ? `${invertComma(
                      //以下コンパイルエラーになってしまうため強制的に文字列にしている
                      //isView()で確認しているため問題ない
                      roundOff(props.statesData['high-pressure2'] as string, 1)
                    )}`
                  : '--'}
                <Box
                  component="span"
                  className="unitArea__diagram__background__comp__data__pressure__box__number__unit">
                  &nbsp;
                  {props.pressureUnit === 'MPa'
                    ? props.trans(
                        'ChillerDetail.diagram.body.unit.pressure.mpa'
                      )
                    : props.trans(
                        'ChillerDetail.diagram.body.unit.pressure.bar'
                      )}
                </Box>
              </Grid>
            )}
          </Grid>
          {/** 低圧 */}
          <Grid
            container
            alignItems="center"
            columnSpacing={1}
            className={`unitArea__diagram__background__comp__data__pressure__box ${
              isRun
                ? ''
                : 'unitArea__diagram__background__comp__data__pressure__box--stop'
            }`}>
            <Grid item xs="auto">
              <Box
                className={
                  isRun
                    ? 'unitArea__diagram__background__comp__data__pressure__box__mark--run'
                    : 'unitArea__diagram__background__comp__data__pressure__box__mark--stop'
                }>
                {props.trans(
                  'ChillerDetail.diagram.body.compressorLowPressure'
                )}
              </Box>
            </Grid>
            {props.compressor === 'CM1' ? (
              <Grid
                item
                xs
                className="unitArea__diagram__background__comp__data__pressure__box__number">
                {isView(props.statesData['low-pressure'])
                  ? `${invertComma(
                      //以下コンパイルエラーになってしまうため強制的に文字列にしている
                      //isView()で確認しているため問題ない
                      roundOff(props.statesData['low-pressure'] as string, 1)
                    )}`
                  : '--'}
                <Box
                  component="span"
                  className="unitArea__diagram__background__comp__data__pressure__box__number__unit">
                  &nbsp;
                  {props.pressureUnit === 'MPa'
                    ? props.trans(
                        'ChillerDetail.diagram.body.unit.pressure.mpa'
                      )
                    : props.trans(
                        'ChillerDetail.diagram.body.unit.pressure.bar'
                      )}
                </Box>
              </Grid>
            ) : (
              <Grid
                item
                xs
                className="unitArea__diagram__background__comp__data__pressure__box__number">
                {isView(props.statesData['low-pressure2'])
                  ? `${invertComma(
                      //以下コンパイルエラーになってしまうため強制的に文字列にしている
                      //isView()で確認しているため問題ない
                      roundOff(props.statesData['low-pressure2'] as string, 1)
                    )}`
                  : '--'}
                <Box
                  component="span"
                  className="unitArea__diagram__background__comp__data__pressure__box__number__unit">
                  &nbsp;
                  {props.pressureUnit === 'MPa'
                    ? props.trans(
                        'ChillerDetail.diagram.body.unit.pressure.mpa'
                      )
                    : props.trans(
                        'ChillerDetail.diagram.body.unit.pressure.bar'
                      )}
                </Box>
              </Grid>
            )}
          </Grid>
        </Stack>
      </Grid>
    </Grid>
  );
};

/**
 * コンプレッサー1台表示
 * @param statesData 熱源機の状態値
 * @param pressureUnit 圧力単位
 * @param trans 翻訳関数
 * @returns JSX
 */
const SingleComp = (props: {
  statesData: apiIF.resParameters.rtOUStates;
  pressureUnit: apiIF.res.getSettingUser['unit']['pressure'];
  trans: TFunction;
}) => {
  return (
    <Stack className="unitArea__diagram__background__comp--chiller">
      {/** コンプレッサー */}
      <CompPressure
        statesData={props.statesData}
        pressureUnit={props.pressureUnit}
        compressor="CM1"
        trans={props.trans}
      />
      {/** コンプレッサー背景 */}
      <Grid
        container
        justifyContent="center"
        className="unitArea__diagram__background__comp__data__noChildCompBackground">
        <Grid
          item
          xs={4}
          className={
            props.statesData['compressor-run-stop'] === 'run'
              ? 'unitArea__diagram__background__comp__data__noChildCompBackground--hydrolutionPRORun'
              : 'unitArea__diagram__background__comp__data__noChildCompBackground--hydrolutionPROStop'
          }></Grid>
      </Grid>
    </Stack>
  );
};

/**
 * コンプレッサー2台表示
 * @param statesData 熱源機の状態値
 * @param pressureUnit 圧力単位
 * @param trans 翻訳関数
 * @returns JSX
 */
const MultiComp = (props: {
  statesData: apiIF.resParameters.rtOUStates;
  pressureUnit: apiIF.res.getSettingUser['unit']['pressure'];
  trans: TFunction;
}) => {
  return (
    <Stack className="unitArea__diagram__background__comp--chiller">
      <Grid container justifyContent="center" columnSpacing={2}>
        {/** コンプレッサー1 */}
        <Grid item xs="auto">
          <CompPressure
            statesData={props.statesData}
            pressureUnit={props.pressureUnit}
            compressor="CM1"
            trans={props.trans}
          />
        </Grid>
        {/** コンプレッサー 2*/}
        <Grid item xs="auto">
          <CompPressure
            statesData={props.statesData}
            pressureUnit={props.pressureUnit}
            compressor="CM2"
            trans={props.trans}
          />
        </Grid>
      </Grid>
      {/** コンプレッサー背景 */}
      <Grid
        container
        justifyContent="center"
        className="unitArea__diagram__background__comp__data__noChildCompBackground">
        {/** コンプレッサー1 */}
        <Grid
          item
          xs={4}
          className={
            props.statesData['compressor-run-stop'] === 'run'
              ? 'unitArea__diagram__background__comp__data__noChildCompBackground--run'
              : 'unitArea__diagram__background__comp__data__noChildCompBackground--stop'
          }></Grid>
        {/** コンプレッサー 2*/}
        <Grid
          item
          xs={4}
          className={
            props.statesData['compressor-run-stop2'] === 'run'
              ? 'unitArea__diagram__background__comp__data__noChildCompBackground--run'
              : 'unitArea__diagram__background__comp__data__noChildCompBackground--stop'
          }></Grid>
      </Grid>
    </Stack>
  );
};

/**
 * 水温、流量表示
 * @param statesData 熱源機の状態値
 * @param trans 翻訳関数
 * @returns JSX
 */
const WaterTank = (props: {
  statesData: apiIF.resParameters.rtOUStates;
  trans: TFunction;
}) => {
  return (
    <Box
      className={
        props.statesData['operation-mode'] === 'cooling'
          ? 'unitArea__diagram__background__tank--cooling'
          : 'unitArea__diagram__background__tank--heating'
      }>
      <Box className="unitArea__diagram__background__tank__data">
        <Stack
          spacing={3}
          className="unitArea__diagram__background__tank__data__inOutTemperature">
          {/** 出口温度 */}
          <Box
            className={
              props.statesData['operation-mode'] === 'cooling'
                ? 'unitArea__diagram__background__tank__data__inOutTemperature__outlet__title--cooling'
                : 'unitArea__diagram__background__tank__data__inOutTemperature__outlet__title--heating'
            }>
            {props.trans('ChillerDetail.diagram.body.outletTemp')}
          </Box>
          <Box className="unitArea__diagram__background__tank__data__inOutTemperature__outlet__number--chiller">
            {isView(props.statesData['outlet-water-temperature'])
              ? invertComma(
                  //以下コンパイルエラーになってしまうため強制的に文字列にしている
                  //isView()で確認しているため問題ない
                  roundOff(
                    props.statesData['outlet-water-temperature'] as string,
                    1
                  )
                )
              : '--'}
            &nbsp;{props.trans('ChillerDetail.diagram.body.unit.temp')}
          </Box>
          {/** 水量 */}
          <Grid
            container
            className="unitArea__diagram__background__tank__data__flowRate">
            <Grid item xs={5}>
              <Box className="unitArea__diagram__background__tank__data__flowRate__number">
                {isView(props.statesData['flow-rate'])
                  ? invertComma(
                      //以下コンパイルエラーになってしまうため強制的に文字列にしている
                      //isView()で確認しているため問題ない
                      roundOff(props.statesData['flow-rate'] as string, 1)
                    )
                  : '--'}
                &nbsp;
                {props.trans('ChillerDetail.diagram.body.unit.flowRate')}
              </Box>
            </Grid>
            <Grid
              item
              xs={7}
              className="unitArea__diagram__background__tank__data__flowRate__title">
              {props.trans('ChillerDetail.diagram.body.flowRate')}
            </Grid>
          </Grid>
          {/** 入口温度 */}
          <Box className="unitArea__diagram__background__tank__data__inOutTemperature__inlet__number--chiller">
            {isView(props.statesData['inlet-water-temperature'])
              ? invertComma(
                  //以下コンパイルエラーになってしまうため強制的に文字列にしている
                  //isView()で確認しているため問題ない
                  roundOff(
                    props.statesData['inlet-water-temperature'] as string,
                    1
                  )
                )
              : '--'}
            &nbsp;{props.trans('ChillerDetail.diagram.body.unit.temp')}
          </Box>
          <Box
            className={
              props.statesData['operation-mode'] === 'cooling'
                ? 'unitArea__diagram__background__tank__data__inOutTemperature__inlet__title--cooling'
                : 'unitArea__diagram__background__tank__data__inOutTemperature__inlet__title--heating'
            }>
            {props.trans('ChillerDetail.diagram.body.inletTemp')}
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default ChillerDetail;
